import React from "react";

import {
  AccordionGroup,
  Alert,
  Heading,
  Stack,
  Text,
} from "@origin-digital/ods-core";

import {
  IIBatterUsageResponse,
  IIBatterySizePrice,
} from "@/contract/generated/electrify-v1";
import { BatteryAccordion } from "@/features/SharedComponents/BatteryAccordion";
import { Outcome } from "@/features/BatteryOutlook/components/Outcome";
import { OutcomeAlert } from "@/features/BatteryOutlook/components/OutcomeAlert";
import { BatteryScenario } from "@/features/BatteryOutlook/types";

import { batterySupported as content } from "./content.json";

export const getBatteryScenario = (
  excessExport: number,
  state: string
): BatteryScenario => {
  if (excessExport < 5) {
    return BatteryScenario.BATTERY_NOT_SUPPORTED;
  }

  if (excessExport >= 5 && excessExport < 7 && ["VIC", "NSW"].includes(state)) {
    return BatteryScenario.MAY_SUPPORT_BATTERY;
  }

  if (
    excessExport >= 5 &&
    excessExport < 7 &&
    ["ACT", "SA", "QLD"].includes(state)
  ) {
    return BatteryScenario.WILL_SUPPORT_BATTERY;
  }

  if (excessExport >= 7) {
    return BatteryScenario.BIG_CAPACITY_TO_SUPPORT_BATTERY;
  }

  throw new Error("Unknown battery scenario");
};

export const BatterySupported = ({
  batteryOutlook,
}: {
  batteryOutlook: IIBatterUsageResponse;
}) => {
  const { currentExcessExport, state, batteryData } = batteryOutlook;
  const batteryScenario = getBatteryScenario(currentExcessExport!, state!);

  const batteries = batteryData
    ? batteryData.sort((a, b) => Number(b.batterySize) - Number(a.batterySize))
    : [];
  const batterySizes = batteries.map((b) => Number(b.batterySize));
  const biggestBatterySize = Math.max(...batterySizes);

  const batteryAccordions = batteries.map((b: IIBatterySizePrice) => {
    const { batterySize } = b;
    const accordionId = `${batterySize}-kw-battery-accordion`;
    const defaultExpanded = Number(batterySize) === biggestBatterySize;
    return (
      <BatteryAccordion
        key={accordionId}
        accordionId={accordionId}
        defaultExpanded={defaultExpanded}
        batterySizePrice={b}
      />
    );
  });

  const scenarioContent = content[batteryScenario];

  const shouldRenderOutcomeAlert = [
    BatteryScenario.MAY_SUPPORT_BATTERY,
    BatteryScenario.WILL_SUPPORT_BATTERY,
  ].includes(batteryScenario);
  return (
    <Stack space="large">
      <Stack space="medium">
        <Heading variant="h2" weight="medium" tone="primaryB">
          {content.heading}
        </Heading>
        <Text tone="neutralDark" variant="subtitle-small">
          {scenarioContent.description}
        </Text>
      </Stack>

      <Outcome
        biggestBatterySize={biggestBatterySize}
        currentExcessExport={currentExcessExport!}
      />

      {shouldRenderOutcomeAlert && (
        <OutcomeAlert batteryScenario={batteryScenario} state={state!} />
      )}

      <Text tone="neutralDark">{content.batteryCostText}</Text>

      <AccordionGroup>{batteryAccordions}</AccordionGroup>

      <Alert tone="info" title={content.keepInMindAlert.title}>
        <Stack space="xsmall">
          <Text tone="neutralDark" variant="body-small">
            {content.keepInMindAlert.installationCostsText}
          </Text>
          <Text tone="neutralDark" variant="body-small">
            {content.keepInMindAlert.provideDetailedQuoteText}
          </Text>
        </Stack>
      </Alert>

      <Text tone="neutralDark" align={{ xs: "left", sm: "center" }}>
        {content.notLockingAnythingText}
      </Text>
    </Stack>
  );
};
